import React, { useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { T } from '../theme/radar';

// Andamento storico di una misura, mese per mese: un grafico a linea piccolo,
// una serie sola (quindi niente legenda), stile piatto del Radar.
// Specifiche: linea 2px, punti r=4 con anello del colore di sfondo, griglia a
// filetto, etichetta solo sull'ultimo valore, tooltip al passaggio del mouse.
// Il colore sta nel segno, mai nel testo.

export interface SerieMensile {
  label: string;
  /** Unità mostrata accanto ai valori (gg, %, …) */
  unita?: string;
  /** Colore della linea (quello della fase) */
  colore: string;
  /** true = un valore più basso è un miglioramento (giorni, non realizzati) */
  megliSeScende: boolean;
  /** Uno per mese, in ordine; null = mese senza dato (buco nella linea) */
  punti: { mese: string; valore: number | null }[];
  /** Spiegazione della misura (tooltip al passaggio del mouse) */
  title?: string;
  decimali?: number;
}

const W = 260;
const H = 84;
const PAD = { top: 10, right: 30, bottom: 18, left: 6 };

const meseBreve = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'short' }).replace('.', '');
};

const fmt = (n: number, dec: number) => n.toLocaleString('it-IT', { maximumFractionDigits: dec });

const Grafico: React.FC<{ serie: SerieMensile }> = ({ serie }) => {
  const [hover, setHover] = useState<number | null>(null);
  const dec = serie.decimali ?? 1;
  const validi = serie.punti.filter((p) => p.valore !== null) as { mese: string; valore: number }[];
  const max = Math.max(1, ...validi.map((p) => p.valore));
  const min = Math.min(0, ...validi.map((p) => p.valore));
  // Asse da zero: le grandezze sono conteggi, giorni o percentuali; scende
  // sotto lo zero solo se serve ("montato dopo l'uscita", "completo prima
  // del mese"), altrimenti quei punti finivano fuori dal grafico.
  const top = Math.ceil(max * 1.15);
  const bottom = min < 0 ? Math.floor(min * 1.15) : 0;
  const n = serie.punti.length;
  const x = (i: number) => PAD.left + (n <= 1 ? 0 : (i * (W - PAD.left - PAD.right)) / (n - 1));
  const y = (v: number) => PAD.top + (1 - (v - bottom) / (top - bottom)) * (H - PAD.top - PAD.bottom);

  // Segmenti continui (un buco spezza la linea invece di interpolarlo)
  const segmenti: string[] = [];
  let corrente: string[] = [];
  serie.punti.forEach((p, i) => {
    if (p.valore === null) { if (corrente.length) segmenti.push(corrente.join(' ')); corrente = []; return; }
    corrente.push(`${x(i)},${y(p.valore)}`);
  });
  if (corrente.length) segmenti.push(corrente.join(' '));

  // Variazione tra gli ultimi due mesi con dato: meglio, peggio o stabile
  const ultimo = validi[validi.length - 1];
  const penultimo = validi[validi.length - 2];
  let delta: { segno: 'meglio' | 'peggio' | 'stabile'; testo: string } | null = null;
  if (ultimo && penultimo) {
    const diff = ultimo.valore - penultimo.valore;
    const soglia = Math.max(0.05, Math.abs(penultimo.valore) * 0.03);
    const segno = Math.abs(diff) <= soglia ? 'stabile' : (diff < 0) === serie.megliSeScende ? 'meglio' : 'peggio';
    delta = { segno, testo: `${diff > 0 ? '+' : ''}${fmt(diff, dec)}${serie.unita ? ' ' + serie.unita : ''} vs ${meseBreve(penultimo.mese)}` };
  }
  const coloreDelta = delta?.segno === 'meglio' ? T.success : delta?.segno === 'peggio' ? T.danger : T.inkFaint;
  const IconaDelta = delta?.segno === 'meglio' ? (serie.megliSeScende ? ArrowDownRight : ArrowUpRight)
    : delta?.segno === 'peggio' ? (serie.megliSeScende ? ArrowUpRight : ArrowDownRight) : Minus;

  // Ultimo mese CON dato (l'ultimo della serie può essere un buco)
  const ultimoIdx = ultimo ? serie.punti.findIndex((p) => p.mese === ultimo.mese) : -1;
  const hoverP = hover !== null ? serie.punti[hover] : null;

  // Nessun mese con dato: niente assi vuoti, solo l'etichetta e una riga
  if (validi.length === 0) {
    return (
      <div className="min-w-0" title={serie.title}>
        <div className="flex items-center gap-1.5" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.1, textTransform: 'uppercase', color: T.inkFaint }}>
          <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: serie.colore }} />
          {serie.label}
        </div>
        <div className="mt-1 text-sm" style={{ color: T.inkFaint }}>in raccolta, compare dal primo mese con dati</div>
      </div>
    );
  }

  return (
    <div className="min-w-0" title={serie.title}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.1, textTransform: 'uppercase', color: T.inkFaint }}>
          <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: serie.colore }} />
          <span className="truncate">{serie.label}</span>
        </div>
        {delta && (
          <span className="inline-flex items-center gap-0.5 text-[11px] whitespace-nowrap" style={{ color: coloreDelta }} title={delta.testo}>
            <IconaDelta size={12} strokeWidth={2} />
            {delta.segno === 'meglio' ? 'meglio' : delta.segno === 'peggio' ? 'peggio' : 'stabile'}
          </span>
        )}
      </div>

      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-xl font-semibold tabular-nums leading-none" style={{ color: T.ink, letterSpacing: '-0.02em' }}>
          {hoverP && hoverP.valore !== null ? fmt(hoverP.valore, dec) : ultimo ? fmt(ultimo.valore, dec) : '—'}
        </span>
        {serie.unita && <span className="text-xs" style={{ color: T.inkFaint }}>{serie.unita}</span>}
        <span className="text-[11px] ml-auto" style={{ color: T.inkFaint }}>
          {hoverP ? meseBreve(hoverP.mese) : delta ? delta.testo : ultimo ? meseBreve(ultimo.mese) : ''}
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-1" style={{ height: H }} onMouseLeave={() => setHover(null)}>
        {/* Griglia: zero e metà, a filetto */}
        {[0, 0.5].map((f) => (
          <line key={f} x1={PAD.left} x2={W - PAD.right} y1={y(top * f)} y2={y(top * f)} stroke={T.borderSoft} strokeWidth={1} />
        ))}
        {segmenti.map((d, i) => (
          <polyline key={i} points={d} fill="none" stroke={serie.colore} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {serie.punti.map((p, i) => p.valore === null ? null : (
          <g key={p.mese}>
            {/* Anello del colore di sfondo, poi il punto; l'area di hover è più larga del segno */}
            <circle cx={x(i)} cy={y(p.valore)} r={hover === i ? 5.5 : 4} fill={serie.colore} stroke={T.surface} strokeWidth={2} />
            <rect x={x(i) - 14} y={0} width={28} height={H} fill="transparent" onMouseEnter={() => setHover(i)} />
          </g>
        ))}
        {/* Etichetta solo sull'ultimo valore */}
        {ultimo && ultimoIdx >= 0 && (
          <text x={x(ultimoIdx) + 8} y={y(ultimo.valore) + 3.5} fontSize={10} fill={T.inkMuted} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {fmt(ultimo.valore, dec)}
          </text>
        )}
        {/* Mesi in basso */}
        {serie.punti.map((p, i) => (
          <text key={p.mese} x={x(i)} y={H - 4} fontSize={9.5} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fill={hover === i ? T.ink : T.inkFaint}>
            {meseBreve(p.mese)}
          </text>
        ))}
      </svg>

    </div>
  );
};

// colonne = 1: impilati (sotto il titolo di un passaggio); altrimenti a griglia
const AndamentoMensile: React.FC<{ serie: SerieMensile[]; colonne?: 1 | 3 }> = ({ serie, colonne = 3 }) => (
  <div className={colonne === 1 ? 'space-y-5' : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-6'}>
    {serie.map((s) => <Grafico key={s.label} serie={s} />)}
  </div>
);

export default AndamentoMensile;
